/**
 * Charts — the brand's own plotting primitives, drawn in SVG.
 *
 * No chart library. Three reasons, in order of how much they matter:
 *
 *   1. Every charting package ships its own visual opinions — its greys, its
 *      grid weight, its tooltip chrome, its default palette. Fighting those
 *      back to the brandbook costs more than drawing the marks ourselves, and
 *      the fight is never quite won.
 *   2. A chart here has to be able to say "no evidence". A library plots a
 *      gap as a gap; we need it to be labelled as a gap, because an empty
 *      quarter in a rent series is a fact about our data, not about the market.
 *   3. React 19 with no peer support yet across the usual suspects.
 *
 * The house style, from the brandbook: neutral structure, red as the one
 * accent. So axes, grids and reference lines are grey and nearly silent, the
 * subject property's own line is the bright one, and red marks the single
 * series or cell the panel exists to draw the eye to — never every series.
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import * as B from "./brand";

/* ── measurement ──────────────────────────────────────────────────── */
/** Width of a container, so a chart can be responsive without a library. */
export function useWidth(fallback = 640) {
  const ref = useRef(null);
  const [w, setW] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const set = () => setW(Math.max(0, Math.floor(el.getBoundingClientRect().width)));
    set();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w || fallback];
}

/* ── scales and ticks ─────────────────────────────────────────────── */
/** Round axis bounds out to human numbers, and return the ticks between. */
export function niceScale(min, max, count = 4) {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return { lo: 0, hi: 1, ticks: [0, 1] };
  if (min === max) {
    const pad = Math.abs(min) * 0.1 || 1;
    min -= pad; max += pad;
  }
  const raw = (max - min) / Math.max(1, count);
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / mag;
  const step = (norm >= 5 ? 10 : norm >= 2 ? 5 : norm >= 1 ? 2 : 1) * mag;
  const lo = Math.floor(min / step) * step;
  const hi = Math.ceil(max / step) * step;
  const ticks = [];
  for (let v = lo; v <= hi + step / 2; v += step) ticks.push(Number(v.toPrecision(12)));
  return { lo, hi, ticks };
}

const PATH = (pts) => pts.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" ");

/* ── the legend, shared ───────────────────────────────────────────── */
export function Legend({ items, className = "" }) {
  return (
    <div className={`flex flex-wrap items-center gap-x-4 gap-y-1.5 ${className}`}>
      {items.filter(Boolean).map((it) => (
        <span key={it.label} className="inline-flex items-center gap-1.5 text-[10.5px] text-[#8a8a8a]">
          <span
            className="inline-block rounded-sm"
            style={{
              width: it.shape === "dot" ? 6 : 12,
              height: it.shape === "dot" ? 6 : it.dashed ? 0 : 2,
              borderRadius: it.shape === "dot" ? 999 : 2,
              background: it.dashed ? "none" : it.color,
              borderTop: it.dashed ? `2px dashed ${it.color}` : "none",
            }}
          />
          {it.label}
        </span>
      ))}
    </div>
  );
}

/* ── "how this is computed" ───────────────────────────────────────── */
/**
 * Every derived figure on a chart can be interrogated. Not a tooltip with a
 * definition — the actual arithmetic, with the counts that went into it, so
 * the reader is checking a method rather than trusting a number.
 */
export function Explain({ children, label = "how this is computed" }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);
  useEffect(() => {
    if (!open) return;
    const key = (e) => e.key === "Escape" && setOpen(false);
    const down = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener("keydown", key);
    document.addEventListener("mousedown", down);
    return () => { document.removeEventListener("keydown", key); document.removeEventListener("mousedown", down); };
  }, [open]);
  return (
    <span ref={wrap} className="relative inline-flex">
      <button type="button" onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className={`text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 rounded transition ${
          open ? "text-[#f04f44] bg-[rgba(233,53,41,0.10)]" : "text-[#7a7a7a] hover:text-[#b4b4b4]"}`}>
        {label}
      </button>
      {open && (
        <span role="tooltip"
          className="absolute right-0 top-[calc(100%+6px)] z-50 w-[19rem] rounded-lg border border-[#3a3a3a] bg-[#1c1c1c] p-3 text-[11.5px] leading-relaxed text-[#b4b4b4] shadow-xl">
          {children}
        </span>
      )}
    </span>
  );
}

/* ── the empty state, shared by every chart ───────────────────────── */
function NoEvidence({ h, children }) {
  return (
    <div className="flex items-center justify-center text-center px-6 text-[11.5px] text-[#7a7a7a] leading-relaxed"
         style={{ height: h }}>
      {children}
    </div>
  );
}

/* ── time series ──────────────────────────────────────────────────── */
/**
 * Lines over time, with an optional shaded band for a range.
 *
 * `series`: [{ key, label, color, points: [{x, y, n}], dashed, area, dots }]
 * `band`:   { color, points: [{ x, lo, hi }] }   — e.g. a comp interquartile
 *
 * x is a number (epoch ms works). A series with a gap is drawn with a gap,
 * not bridged: a quarter with nothing signed is not a straight line between
 * its neighbours.
 */
export function TimeSeries({
  series = [], band = null, height = 200, yFmt = (v) => v, xFmt = (v) => v,
  yZero = false, empty = "Nothing to plot.", markers = [],
}) {
  const [ref, w] = useWidth();
  const [hoverX, setHoverX] = useState(null);

  const all = series.flatMap((s) => s.points || []);
  const usable = all.filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));

  const m = { l: 46, r: 12, t: 12, b: 22 };
  const iw = Math.max(10, w - m.l - m.r);
  const ih = Math.max(10, height - m.t - m.b);

  const geom = useMemo(() => {
    if (!usable.length) return null;
    const xs = usable.map((p) => p.x);
    const bandYs = band ? (band.points || []).flatMap((p) => [p.lo, p.hi]) : [];
    const ys = usable.map((p) => p.y).concat(bandYs.filter(Number.isFinite));
    const x0 = Math.min(...xs), x1 = Math.max(...xs);
    const { lo, hi, ticks } = niceScale(yZero ? 0 : Math.min(...ys), Math.max(...ys), 4);
    const sx = (x) => m.l + (x1 === x0 ? iw / 2 : ((x - x0) / (x1 - x0)) * iw);
    const sy = (y) => m.t + ih - ((y - lo) / (hi - lo || 1)) * ih;
    return { x0, x1, lo, hi, ticks, sx, sy };
  }, [usable, band, iw, ih, yZero, m.l, m.t]);

  if (!usable.length) return <div ref={ref}><NoEvidence h={height}>{empty}</NoEvidence></div>;

  const { x0, x1, ticks, sx, sy } = geom;

  // Four x labels, evenly spaced across the actual domain.
  const xTicks = [0, 1, 2, 3].map((i) => x0 + ((x1 - x0) * i) / 3);

  // Nearest point on the first series that has one, for the readout.
  const hovered = hoverX == null ? null : series.map((s) => {
    const pts = (s.points || []).filter((p) => Number.isFinite(p.y));
    if (!pts.length) return null;
    const near = pts.reduce((a, p) => (Math.abs(p.x - hoverX) < Math.abs(a.x - hoverX) ? p : a));
    return { s, p: near };
  }).filter(Boolean);
  const anchor = hovered && hovered.length ? hovered[0].p.x : null;

  return (
    <div ref={ref} className="relative">
      <svg width="100%" height={height} viewBox={`0 0 ${w} ${height}`} role="img"
        onMouseMove={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          const px = ((e.clientX - r.left) / r.width) * w;
          setHoverX(x0 + ((px - m.l) / iw) * (x1 - x0));
        }}
        onMouseLeave={() => setHoverX(null)}>

        {/* grid — quiet, behind everything */}
        {ticks.map((t) => (
          <g key={t}>
            <line x1={m.l} x2={w - m.r} y1={sy(t)} y2={sy(t)} stroke={B.ink.lineSoft} strokeWidth="1" />
            <text x={m.l - 8} y={sy(t) + 3.5} textAnchor="end" fill={B.ink.muted}
              style={{ fontSize: 9.5, letterSpacing: 0.2 }}>{yFmt(t)}</text>
          </g>
        ))}
        {xTicks.map((t, i) => (
          <text key={i} x={sx(t)} y={height - 6} textAnchor={i === 0 ? "start" : i === 3 ? "end" : "middle"}
            fill={B.ink.muted} style={{ fontSize: 9.5 }}>{xFmt(t)}</text>
        ))}

        {/* vertical markers — "we underwrote here", "acquisition" */}
        {markers.map((mk, i) => Number.isFinite(mk.x) && (
          <g key={i}>
            <line x1={sx(mk.x)} x2={sx(mk.x)} y1={m.t} y2={m.t + ih}
              stroke={B.red.edge} strokeWidth="1" strokeDasharray="3 3" />
            {mk.label && (
              <text x={sx(mk.x) + 4} y={m.t + 9} fill={B.red.light} style={{ fontSize: 9 }}>{mk.label}</text>
            )}
          </g>
        ))}

        {/* the range band, if one was given */}
        {band && band.points && band.points.length > 1 && (
          <path
            d={`${PATH(band.points.map((p) => [sx(p.x), sy(p.hi)]))} L${sx(band.points[band.points.length - 1].x).toFixed(1)} ${sy(band.points[band.points.length - 1].lo).toFixed(1)} ${band.points.slice().reverse().slice(1).map((p) => `L${sx(p.x).toFixed(1)} ${sy(p.lo).toFixed(1)}`).join(" ")} Z`}
            fill={band.color || "rgba(244,244,244,0.05)"} stroke="none" />
        )}

        {series.map((s) => {
          const pts = (s.points || []);
          // Split on gaps so a missing period reads as missing.
          const runs = [];
          let cur = [];
          pts.forEach((p) => {
            if (Number.isFinite(p.y)) cur.push([sx(p.x), sy(p.y)]);
            else if (cur.length) { runs.push(cur); cur = []; }
          });
          if (cur.length) runs.push(cur);
          return (
            <g key={s.key}>
              {s.area && runs.map((r, i) => r.length > 1 && (
                <path key={i} d={`${PATH(r)} L${r[r.length - 1][0].toFixed(1)} ${(m.t + ih).toFixed(1)} L${r[0][0].toFixed(1)} ${(m.t + ih).toFixed(1)} Z`}
                  fill={s.area} stroke="none" />
              ))}
              {runs.map((r, i) => (
                r.length > 1
                  ? <path key={i} d={PATH(r)} fill="none" stroke={s.color} strokeWidth={s.width || 1.75}
                      strokeLinejoin="round" strokeLinecap="round"
                      strokeDasharray={s.dashed ? "4 4" : undefined} />
                  : <circle key={i} cx={r[0][0]} cy={r[0][1]} r="2.5" fill={s.color} />
              ))}
              {s.dots && runs.flat().map((p, i) => (
                <circle key={i} cx={p[0]} cy={p[1]} r="2.2" fill={B.ink.page} stroke={s.color} strokeWidth="1.4" />
              ))}
            </g>
          );
        })}

        {/* the crosshair */}
        {anchor != null && (
          <g>
            <line x1={sx(anchor)} x2={sx(anchor)} y1={m.t} y2={m.t + ih} stroke={B.ink.line} strokeWidth="1" />
            {hovered.map(({ s, p }) => Number.isFinite(p.y) && (
              <circle key={s.key} cx={sx(p.x)} cy={sy(p.y)} r="3.2" fill={s.color} stroke={B.ink.page} strokeWidth="1.2" />
            ))}
          </g>
        )}
      </svg>

      {anchor != null && hovered.length > 0 && (
        <div className="pointer-events-none absolute z-20 rounded-lg border border-[#3a3a3a] bg-[#1c1c1c] px-2.5 py-2 shadow-xl"
          style={{
            left: Math.min(Math.max(sx(anchor) + 10, 4), Math.max(4, w - 176)),
            top: 6, width: 168,
          }}>
          <div className="text-[10px] uppercase tracking-[0.1em] text-[#8a8a8a]">{xFmt(anchor)}</div>
          {hovered.map(({ s, p }) => (
            <div key={s.key} className="mt-1 flex items-baseline justify-between gap-2">
              <span className="inline-flex items-center gap-1.5 text-[10.5px] text-[#8a8a8a] min-w-0">
                <span className="inline-block h-[2px] w-2.5 shrink-0" style={{ background: s.color }} />
                <span className="truncate">{s.label}</span>
              </span>
              <span className="text-[11.5px] tabular-nums text-[#f4f4f4] shrink-0">
                {Number.isFinite(p.y) ? yFmt(p.y) : "—"}
                {p.n ? <span className="text-[9.5px] text-[#7a7a7a]"> ·{p.n}</span> : null}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── scatter, with a fitted trend ─────────────────────────────────── */
/**
 * Individual events as marks. Used for signings: one dot per lease, sized by
 * area, so a 90,000 SF signing does not read the same as a 1,200 SF one.
 *
 * `points`: [{ x, y, size, tone, label }]
 * `trend`:  [{ x, y }] — drawn as a line; compute it in the service, not here
 * `refLines`: [{ y, label, color, dashed }]
 */
export function Scatter({
  points = [], trend = null, refLines = [], markers = [], height = 220,
  yFmt = (v) => v, xFmt = (v) => v, empty = "Nothing to plot.", onHover,
}) {
  const [ref, w] = useWidth();
  const [hi, setHi] = useState(null);

  const usable = points.filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
  const m = { l: 46, r: 12, t: 12, b: 22 };
  const iw = Math.max(10, w - m.l - m.r);
  const ih = Math.max(10, height - m.t - m.b);

  if (!usable.length) return <div ref={ref}><NoEvidence h={height}>{empty}</NoEvidence></div>;

  const xs = usable.map((p) => p.x);
  const x0 = Math.min(...xs), x1 = Math.max(...xs);
  const ys = usable.map((p) => p.y)
    .concat(refLines.map((r) => r.y).filter(Number.isFinite))
    .concat((trend || []).map((t) => t.y));
  const { lo, hi: yhi, ticks } = niceScale(Math.min(...ys), Math.max(...ys), 4);
  const sx = (x) => m.l + (x1 === x0 ? iw / 2 : ((x - x0) / (x1 - x0)) * iw);
  const sy = (y) => m.t + ih - ((y - lo) / (yhi - lo || 1)) * ih;

  const maxSize = Math.max(1, ...usable.map((p) => p.size || 1));
  const r = (p) => 2.4 + 5.6 * Math.sqrt((p.size || 1) / maxSize);
  const xTicks = [0, 1, 2, 3].map((i) => x0 + ((x1 - x0) * i) / 3);

  return (
    <div ref={ref} className="relative">
      <svg width="100%" height={height} viewBox={`0 0 ${w} ${height}`} role="img">
        {ticks.map((t) => (
          <g key={t}>
            <line x1={m.l} x2={w - m.r} y1={sy(t)} y2={sy(t)} stroke={B.ink.lineSoft} strokeWidth="1" />
            <text x={m.l - 8} y={sy(t) + 3.5} textAnchor="end" fill={B.ink.muted} style={{ fontSize: 9.5 }}>{yFmt(t)}</text>
          </g>
        ))}
        {xTicks.map((t, i) => (
          <text key={i} x={sx(t)} y={height - 6} textAnchor={i === 0 ? "start" : i === 3 ? "end" : "middle"}
            fill={B.ink.muted} style={{ fontSize: 9.5 }}>{xFmt(t)}</text>
        ))}

        {markers.map((mk, i) => Number.isFinite(mk.x) && mk.x >= x0 && mk.x <= x1 && (
          <g key={`m${i}`}>
            <line x1={sx(mk.x)} x2={sx(mk.x)} y1={m.t} y2={m.t + ih}
              stroke={B.red.edge} strokeWidth="1" strokeDasharray="3 3" />
            {mk.label && (
              <text x={sx(mk.x) + 4} y={m.t + 9} fill={B.red.light} style={{ fontSize: 9 }}>{mk.label}</text>
            )}
          </g>
        ))}

        {refLines.map((rl, i) => Number.isFinite(rl.y) && (
          <g key={i}>
            <line x1={m.l} x2={w - m.r} y1={sy(rl.y)} y2={sy(rl.y)}
              stroke={rl.color || B.ink.line} strokeWidth="1.25"
              strokeDasharray={rl.dashed === false ? undefined : "5 4"} />
            {rl.label && (
              <text x={w - m.r} y={sy(rl.y) - 4} textAnchor="end" fill={rl.color || B.ink.muted}
                style={{ fontSize: 9.5 }}>{rl.label}</text>
            )}
          </g>
        ))}

        {trend && trend.length > 1 && (
          <path d={PATH(trend.map((t) => [sx(t.x), sy(t.y)]))} fill="none"
            stroke={B.ink.bright} strokeWidth="1.5" strokeDasharray="5 4" opacity="0.8" />
        )}

        {usable.map((p, i) => (
          <circle key={i} cx={sx(p.x)} cy={sy(p.y)} r={r(p)}
            fill={p.tone || "rgba(244,244,244,0.16)"}
            stroke={p.stroke || (p.tone ? "none" : "rgba(244,244,244,0.45)")}
            strokeWidth="1"
            onMouseEnter={() => { setHi({ p, px: sx(p.x), py: sy(p.y) }); onHover?.(p); }}
            onMouseLeave={() => { setHi(null); onHover?.(null); }}
            style={{ cursor: p.label ? "pointer" : "default" }} />
        ))}
      </svg>

      {hi && hi.p.label && (
        <div className="pointer-events-none absolute z-20 rounded-lg border border-[#3a3a3a] bg-[#1c1c1c] px-2.5 py-1.5 shadow-xl"
          style={{ left: Math.min(Math.max(hi.px + 10, 4), Math.max(4, w - 190)), top: Math.max(2, hi.py - 34), width: 182 }}>
          <div className="text-[11px] text-[#f4f4f4] truncate">{hi.p.label}</div>
          <div className="text-[10.5px] text-[#8a8a8a]">
            {yFmt(hi.p.y)} · {xFmt(hi.p.x)}{hi.p.size ? ` · ${Math.round(hi.p.size).toLocaleString()} SF` : ""}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── columns ──────────────────────────────────────────────────────── */
/** Vertical bars with an optional second, paler bar behind for a comparison. */
export function Columns({
  data = [], height = 180, yFmt = (v) => v, empty = "Nothing to plot.",
  accentIndex = -1, cumulative = null,
}) {
  const [ref, w] = useWidth();
  const usable = data.filter((d) => Number.isFinite(d.value));
  const m = { l: 44, r: 12, t: 12, b: 24 };
  const iw = Math.max(10, w - m.l - m.r);
  const ih = Math.max(10, height - m.t - m.b);
  if (!usable.length) return <div ref={ref}><NoEvidence h={height}>{empty}</NoEvidence></div>;

  const { lo, hi, ticks } = niceScale(Math.min(0, ...usable.map((d) => d.value)), Math.max(...usable.map((d) => d.value)), 3);
  const sy = (v) => m.t + ih - ((v - lo) / (hi - lo || 1)) * ih;
  const bw = iw / usable.length;
  const pad = Math.min(8, bw * 0.22);

  return (
    <div ref={ref}>
      <svg width="100%" height={height} viewBox={`0 0 ${w} ${height}`} role="img">
        {ticks.map((t) => (
          <g key={t}>
            <line x1={m.l} x2={w - m.r} y1={sy(t)} y2={sy(t)} stroke={B.ink.lineSoft} strokeWidth="1" />
            <text x={m.l - 8} y={sy(t) + 3.5} textAnchor="end" fill={B.ink.muted} style={{ fontSize: 9.5 }}>{yFmt(t)}</text>
          </g>
        ))}
        {usable.map((d, i) => {
          const x = m.l + i * bw + pad / 2;
          const y = sy(Math.max(0, d.value)), y0 = sy(0);
          const accent = i === accentIndex || d.accent;
          return (
            <g key={i}>
              <rect x={x} y={Math.min(y, y0)} width={Math.max(1, bw - pad)} height={Math.max(1, Math.abs(y0 - y))}
                rx="2" fill={accent ? "rgba(233,53,41,0.55)" : d.color || "rgba(244,244,244,0.20)"} />
              <text x={x + (bw - pad) / 2} y={height - 7} textAnchor="middle" fill={B.ink.muted}
                style={{ fontSize: 9.5 }}>{d.label}</text>
            </g>
          );
        })}
        {cumulative && cumulative.length > 1 && (
          <path d={PATH(cumulative.map((c, i) => [m.l + i * bw + bw / 2, sy(c)]))}
            fill="none" stroke={B.ink.bright} strokeWidth="1.4" strokeDasharray="4 4" opacity="0.75" />
        )}
      </svg>
    </div>
  );
}

/* ── heat grid ────────────────────────────────────────────────────── */
/**
 * A matrix of cells shaded by value. Two uses here: expense categories against
 * the bases they were stated on, and a geographic grid of what expenses run at
 * around a subject property.
 *
 * A cell with no observations is drawn empty and labelled "—", never as a zero
 * or as the midpoint of the scale. That distinction is the whole point.
 *
 * `cells`: [{ row, col, value, n, note }]
 */
export function HeatGrid({
  rows = [], cols = [], cells = [], fmt = (v) => v, height = null,
  rowLabelWidth = 118, empty = "Nothing to plot.", subjectCell = null,
  scale = "sequential", onCell = null,
}) {
  const [hover, setHover] = useState(null);
  const vals = cells.map((c) => c.value).filter(Number.isFinite);
  if (!rows.length || !cols.length || !vals.length) return <NoEvidence h={height || 160}>{empty}</NoEvidence>;

  const lo = Math.min(...vals), hi = Math.max(...vals);
  const mid = vals.slice().sort((a, b) => a - b)[Math.floor(vals.length / 2)];

  // Sequential: neutral -> red, the brand's one accent carrying "more".
  // Diverging: green below the midpoint, red above, for variance against a norm.
  const shade = (v) => {
    if (!Number.isFinite(v)) return "transparent";
    if (scale === "diverging") {
      const d = mid ? (v - mid) / Math.max(1e-9, Math.abs(mid)) : 0;
      const t = Math.max(-1, Math.min(1, d / 0.35));
      return t >= 0
        ? `rgba(233,53,41,${(0.06 + 0.42 * t).toFixed(3)})`
        : `rgba(79,157,118,${(0.06 + 0.42 * -t).toFixed(3)})`;
    }
    const t = hi === lo ? 0.5 : (v - lo) / (hi - lo);
    return `rgba(233,53,41,${(0.05 + 0.45 * t).toFixed(3)})`;
  };

  const byKey = {};
  cells.forEach((c) => { byKey[`${c.row}||${c.col}`] = c; });

  return (
    <div className="relative overflow-x-auto">
      <table className="w-full border-separate" style={{ borderSpacing: 2 }}>
        <thead>
          <tr>
            <th style={{ width: rowLabelWidth }} />
            {cols.map((c) => (
              <th key={c} className="px-1 pb-1 text-[9.5px] font-semibold uppercase tracking-[0.1em] text-[#8a8a8a] text-center">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r}>
              <td className="pr-2 text-[11px] text-[#b4b4b4] truncate" style={{ maxWidth: rowLabelWidth }}>{r}</td>
              {cols.map((c) => {
                const cell = byKey[`${r}||${c}`];
                const v = cell ? cell.value : null;
                const isSubject = subjectCell && subjectCell.row === r && subjectCell.col === c;
                return (
                  <td key={c} className="p-0">
                    <div
                      onMouseEnter={() => cell && setHover({ r, c, cell })}
                      onMouseLeave={() => setHover(null)}
                      onClick={() => cell && onCell?.(cell)}
                      className="rounded-[3px] flex flex-col items-center justify-center transition"
                      style={{
                        height: 34,
                        background: shade(v),
                        border: isSubject ? `1px solid ${B.red.DEFAULT}` : `1px solid ${B.ink.lineSoft}`,
                        cursor: onCell && cell ? "pointer" : "default",
                      }}>
                      <span className="text-[11px] tabular-nums" style={{ color: Number.isFinite(v) ? B.ink.bright : "#6f6f6f" }}>
                        {Number.isFinite(v) ? fmt(v) : "—"}
                      </span>
                      {cell && cell.n > 0 && (
                        <span className="text-[8.5px] text-[#b4b4b4] leading-none">n={cell.n}</span>
                      )}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {hover && hover.cell.note && (
        <div className="mt-2 text-[10.5px] text-[#8a8a8a]">
          <span className="text-[#b4b4b4]">{hover.r} · {hover.c}</span> — {hover.cell.note}
        </div>
      )}
    </div>
  );
}

/* ── funnel ───────────────────────────────────────────────────────── */
/**
 * Stage-to-stage flow. Each stage shows what is in it and what share of the
 * stage before it survived — the number that actually tells you where deals
 * are dying.
 */
export function Funnel({ stages = [], height = 190, valueFmt = (v) => v, empty = "No deals in the pipeline." }) {
  const [ref, w] = useWidth();
  const [hi, setHi] = useState(null);
  const usable = stages.filter((s) => Number.isFinite(s.count));
  if (!usable.length || !usable[0].count) return <div ref={ref}><NoEvidence h={height}>{empty}</NoEvidence></div>;

  // The count sits 5px above its bar, and the tallest bar starts at m.t. At 8
  // the first stage's number had its baseline at y=3, so a 13px label drew
  // above the SVG's top edge and was clipped to a sliver. 22 clears it.
  const m = { t: 22, b: 40 };
  const ih = height - m.t - m.b;
  const sw = w / usable.length;
  const top = Math.max(...usable.map((s) => s.count));
  const h = (n) => Math.max(2, (n / top) * ih);

  return (
    <div ref={ref} className="relative">
      <svg width="100%" height={height} viewBox={`0 0 ${w} ${height}`} role="img">
        <defs>
          <linearGradient id="pp-funnel" x1="0" x2="1">
            <stop offset="0%" stopColor="rgba(233,53,41,0.34)" />
            <stop offset="100%" stopColor="rgba(233,53,41,0.10)" />
          </linearGradient>
        </defs>

        {/* the ribbon: each segment tapers into the next stage's height */}
        {usable.map((s, i) => {
          const next = usable[i + 1];
          if (!next) return null;
          const x0 = i * sw + sw / 2, x1 = (i + 1) * sw + sw / 2;
          const y0 = m.t + (ih - h(s.count)) / 2, y1 = m.t + (ih - h(next.count)) / 2;
          return (
            <path key={`r${i}`}
              d={`M${x0} ${y0} L${x1} ${y1} L${x1} ${y1 + h(next.count)} L${x0} ${y0 + h(s.count)} Z`}
              fill="url(#pp-funnel)" opacity={hi == null || hi === i || hi === i + 1 ? 1 : 0.35} />
          );
        })}

        {usable.map((s, i) => {
          const x = i * sw + sw / 2, hh = h(s.count), y = m.t + (ih - hh) / 2;
          const conv = i === 0 ? null : usable[i - 1].count ? s.count / usable[i - 1].count : null;
          return (
            <g key={s.label} onMouseEnter={() => setHi(i)} onMouseLeave={() => setHi(null)}>
              <rect x={x - 7} y={y} width="14" height={hh} rx="2"
                fill={i === usable.length - 1 ? B.red.DEFAULT : "rgba(244,244,244,0.55)"}
                opacity={hi == null || hi === i ? 1 : 0.4} />
              <text x={x} y={y - 5} textAnchor="middle" fill={B.ink.bright}
                style={{ fontSize: 13, fontWeight: 300 }}>{s.count}</text>
              <text x={x} y={height - 24} textAnchor="middle" fill={B.ink.soft} style={{ fontSize: 9.5 }}>
                {s.label}
              </text>
              <text x={x} y={height - 11} textAnchor="middle" fill={conv == null ? B.ink.muted : conv < 0.35 ? B.red.light : B.ink.muted}
                style={{ fontSize: 9 }}>
                {s.value ? valueFmt(s.value) : conv != null ? `${Math.round(conv * 100)}% carried` : ""}
              </text>
              {/* a wide invisible target so the thin bars are still hoverable */}
              <rect x={i * sw} y={0} width={sw} height={height} fill="transparent" />
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/* ── sparkline ────────────────────────────────────────────────────── */
export function Sparkline({ values = [], width = 74, height = 22, tone = null }) {
  const pts = values.filter(Number.isFinite);
  if (pts.length < 2) return <svg width={width} height={height} />;
  const lo = Math.min(...pts), hi = Math.max(...pts);
  const sx = (i) => (i / (pts.length - 1)) * (width - 2) + 1;
  const sy = (v) => height - 2 - ((v - lo) / (hi - lo || 1)) * (height - 4);
  const rising = pts[pts.length - 1] >= pts[0];
  const color = tone || (rising ? B.signal.up : B.red.DEFAULT);
  return (
    <svg width={width} height={height} role="img" aria-hidden="true">
      <path d={`${PATH(pts.map((v, i) => [sx(i), sy(v)]))} L${sx(pts.length - 1)} ${height} L${sx(0)} ${height} Z`}
        fill={rising ? "rgba(79,157,118,0.10)" : "rgba(233,53,41,0.10)"} />
      <path d={PATH(pts.map((v, i) => [sx(i), sy(v)]))} fill="none" stroke={color} strokeWidth="1.4"
        strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={sx(pts.length - 1)} cy={sy(pts[pts.length - 1])} r="1.8" fill={color} />
    </svg>
  );
}

/* ── stacked bar, single row ──────────────────────────────────────── */
/** One horizontal bar split by share — expense composition, tenure mix. */
export function ShareBar({ parts = [], height = 10, fmt = (v) => v }) {
  const total = parts.reduce((s, p) => s + (Number(p.value) || 0), 0);
  if (!total) return null;
  return (
    <div>
      <div className="flex w-full overflow-hidden rounded-full" style={{ height }}>
        {parts.map((p, i) => (
          <div key={i} title={`${p.label} — ${fmt(p.value)}`}
            style={{ width: `${(p.value / total) * 100}%`, background: p.color }} />
        ))}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
        {parts.map((p, i) => (
          <span key={i} className="inline-flex items-center gap-1.5 text-[10.5px] text-[#8a8a8a]">
            <span className="inline-block h-2 w-2 rounded-sm" style={{ background: p.color }} />
            {p.label}
            <span className="tabular-nums text-[#b4b4b4]">{Math.round((p.value / total) * 100)}%</span>
          </span>
        ))}
      </div>
    </div>
  );
}
